import classNames from 'classnames'
import Highcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import { useCallback, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'

import 'highcharts/modules/accessibility'
import 'highcharts/modules/export-data'
import 'highcharts/modules/exporting'

import type { Chart as ChartApi } from 'highcharts/highstock'
import type { MouseEvent, ReactElement } from 'react'
import type { IAppEvent } from '~/types/event'
import type {
  IBuilderConfig,
  IGetOverallResponse,
  IGetSeriesDataBody,
  IGetSeriesDataResponse,
} from '~/types/graph-builder'

import { useFormattedEventsSeries, useFormattedBody, useOptions } from './utils'

import AddToFavourites from '~/components/AddToFavourite'
import AnalyticBlockMenu from '~/components/AnalyticBlocksMenu'
import AnalyticGenericBlock from '~/components/AnalyticGenericBlock'
import AnalyticTooltip from '~/components/AnalyticsTooltip'
import AnalyticWarningTooltip from '~/components/AnalyticsWarningTooltip'
import DropdownMenu from '~/components/DropdownMenu'
import { dataNotAvailableText, AUTHORIZATIONS } from '~/const'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useGetBuilderBatchDataQuery } from '~/queries/graphs-builder'
import { Mode } from '~/types/graph-builder'
import { isEmptyData } from '~/utils/chart'
import { isIntranet } from '~/utils/env'
import { formatGraphTitle } from '~/utils/graphs-builder'
import { copyText } from '~/utils/navigator'

const containerProps = {
  style: {
    height: '99%',
    width: '100%',
  },
}

interface Props {
  apiSelectionFn?: (body: IGetSeriesDataBody[]) => Promise<any>
  appKey: string
  canBeFavourite?: boolean
  className?: string
  config: IBuilderConfig
  deviceType: string
  eventsData?: IAppEvent[]
  isAppLoading?: boolean
  isDraggable?: boolean
  kpiField: string
  lazy?: boolean
  withDefaultInstallationCountry?: boolean
}

const DynamicGraphBlock = ({
  apiSelectionFn,
  appKey,
  canBeFavourite = true,
  className,
  config,
  deviceType,
  eventsData,
  isAppLoading,
  isDraggable,
  kpiField,
  lazy = true,
  withDefaultInstallationCountry = true,
}: Props): ReactElement => {
  /** Local state */

  const userAuthorizedToEdit = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.WRITE])

  const navigate = useNavigate()

  const { inView, ref } = useInView({ triggerOnce: true })

  const { state } = useAnalyticsContext()

  const [chartApi, setChartApi] = useState<ChartApi>()

  const genericBlockClasses = classNames('dynamic-graph-block-container', className)

  const title = useMemo(() => formatGraphTitle(config.title), [config.title])

  const body = useFormattedBody({ appKey, config, deviceType, kpiField, withDefaultInstallationCountry })

  const isSomeValues = useMemo(() => body.some((el) => el.kpi_values.values.length > 0), [body])

  const isQueryEnabled = useMemo(() => {
    if (isSomeValues && config.series.length > 0 && config.series.some((el) => el.values.length > 0)) {
      return lazy && config.id ? inView : true
    }
    return false
  }, [config.id, config.series, inView, isSomeValues, lazy])

  const menu = useMemo(
    () => [
      ...(userAuthorizedToEdit
        ? [
            {
              items: [
                {
                  label: 'Edit',
                  onClick: () => navigate(`/analytics/ca/builder/${config.id}`),
                },
              ],
            },
          ]
        : []),
      {
        items: [
          {
            label: 'Copy link',
            // NOTE: remove the previous hash from the url to not duplicate it
            onClick: () => copyText(`${window.location.href.split('#')[0]}#${config.id}`, 'Url copied to clipboard!'),
          },
        ],
      },
      {
        items: [
          {
            label: 'Open in fullscreen',
            onClick: () => {
              if (chartApi) {
                chartApi.fullscreen.open()
              }
            },
          },
        ],
      },
      {
        items: [
          { label: 'Download CSV', onClick: () => chartApi?.downloadCSV() },
          { label: 'Download XLS', onClick: () => chartApi?.downloadXLS() },
        ],
        sectionTitle: 'export',
      },
    ],
    [chartApi, config.id, navigate, userAuthorizedToEdit]
  )

  /** Queries & Mutations */

  const queryParams = useMemo(
    () => ({
      body,
      options: { enabled: isQueryEnabled },
      queryFn: apiSelectionFn,
    }),
    [apiSelectionFn, body, isQueryEnabled]
  )

  const {
    data: series,
    isError,
    isLoading: isSeriesLoading,
  } = useGetBuilderBatchDataQuery<IGetSeriesDataResponse[]>({
    ...queryParams,
    mode: Mode.NORMAL,
  })

  const overallBody = {
    ...queryParams,
    body: [
      ...queryParams.body.map((el) => ({ ...el, breakdowns: el.breakdowns.filter((filter) => filter !== 'date') })),
    ],
    mode: Mode.OVERALL,
  }

  const { data: overall, isLoading: isOverallDataLoading } =
    useGetBuilderBatchDataQuery<IGetOverallResponse[]>(overallBody)

  const isLoading = isSeriesLoading || isOverallDataLoading

  const appEventsSeries = useFormattedEventsSeries({ config, eventsData, series })

  const options = useOptions({ appEventsSeries, config, overall, series })

  /** Handlers */

  const handleGraphContainerClick = useCallback((evt: MouseEvent) => evt.stopPropagation(), [])

  const renderChildren = useMemo(() => {
    if (config.requiredBreakdown && !state.filters.breakdown?.includes(config.requiredBreakdown)) {
      return <div>{`Please select breakdown ${config.requiredBreakdown} to display the graph.`}</div>
    }

    if (isError) return <span>API error</span>

    if (!isSomeValues) return <span>Please select some values.</span>

    if (!series || !series.length || series.every((item) => isEmptyData(item)))
      return <span>{dataNotAvailableText}</span>

    return options ? (
      <HighchartsReact
        callback={(chartApi: ChartApi) => setChartApi(chartApi)}
        containerProps={containerProps}
        highcharts={Highcharts}
        options={options}
      />
    ) : null
  }, [config.requiredBreakdown, isError, isSomeValues, options, series, state.filters.breakdown])

  return (
    <>
      {/**
       * This id is used as an anchor.
       * The `useScrollToElement` in the generic `Layout` component will detect if an anchor is present
       * in the url and scroll to it if the anchor is present in the view.
       */}
      {config.id ? <div id={config.id} ref={ref} /> : null}
      <AnalyticGenericBlock
        className={genericBlockClasses}
        isDraggable={isDraggable}
        isLoading={isAppLoading || isLoading}
        title={title}
      >
        <AnalyticBlockMenu>
          {canBeFavourite && isIntranet() && config.id ? <AddToFavourites configId={config.id} /> : null}
          {config.warningMessage ? <AnalyticWarningTooltip message={config.warningMessage} /> : null}
          {config.tooltip?.how || config.tooltip?.what || config.tooltip?.relatedGlossaryItems?.length > 0 ? (
            <AnalyticTooltip value={{ ...config.tooltip, glossarySearch: config.tooltip.relatedGlossaryItems }} />
          ) : null}
          <DropdownMenu menu={menu} onMouseDown={(e: MouseEvent) => e.stopPropagation()} />
        </AnalyticBlockMenu>
        <div className="container graph-container" onMouseDown={handleGraphContainerClick}>
          {renderChildren}
        </div>
      </AnalyticGenericBlock>
    </>
  )
}

export default DynamicGraphBlock
